import type { EmotionLabel } from '../../types';
import { avgKeys, clamp } from '../../LocalEmotionUtils';

export interface EyeContextProfile {
  sampleCount: number;
  auRanges: Record<string, { p10: number; p90: number }>;
  auMedians: Record<string, number>;
  auReliability?: Record<string, number>;
}

export interface AdaptiveCalibrationConfig {
  calibrationQuality: number;
  perEmotionAdaptiveFactors: Partial<Record<Exclude<EmotionLabel, 'neutral' | 'focused'>, number>>;
  minScoreDeltas?: Partial<Record<'sad' | 'angry' | 'happy' | 'surprise', number>>;
  persistenceOverrides?: {
    sadFrames?: number;
    angryExtraFrames?: number;
    committedHoldFrames?: number;
  };
  strongInstantScoreDeltas?: Partial<Record<'sad' | 'angry' | 'happy' | 'surprise', number>>;
}

export interface CalibrationBaseline {
  smile: { p10: number; p90: number };
  frown: { p10: number; p90: number };
  browDown: { p10: number; p90: number };
  browUp: { p10: number; p90: number };
  eyeWide: { p10: number; p90: number };
  eyeSquint: { p10: number; p90: number };
  jawOpen: { p10: number; p90: number };
  noseSneer: { p10: number; p90: number };
  auRanges: Record<string, { p10: number; p90: number }>;
  auMedians: Record<string, number>;
  eyeContextProfiles?: {
    eyeClear?: EyeContextProfile;
    eyeOccluded?: EyeContextProfile;
  };
  adaptiveConfig?: AdaptiveCalibrationConfig;
  timestamp: number;
  gazeOffset?: { h: number; v: number };
}

export interface EyeContextSnapshot {
  eyeReliability: number;
  eyeOccluded: boolean;
  hasOccludedCalibration: boolean;
}

const CALIBRATION_AUS = [
  'mouthSmileLeft', 'mouthSmileRight',
  'cheekSquintLeft', 'cheekSquintRight',
  'eyeSquintLeft', 'eyeSquintRight',
  'mouthFrownLeft', 'mouthFrownRight',
  'mouthShrugLower', 'mouthShrugUpper',
  'browInnerUp',
  'browDownLeft', 'browDownRight',
  'eyeLookDownLeft', 'eyeLookDownRight',
  'mouthRollLower',
  'jawForward', 'jawOpen',
  'mouthPressLeft', 'mouthPressRight',
  'noseSneerLeft', 'noseSneerRight',
  'eyeWideLeft', 'eyeWideRight',
  'browOuterUpLeft', 'browOuterUpRight',
  'mouthStretchLeft', 'mouthStretchRight',
  'mouthUpperUpLeft', 'mouthUpperUpRight',
];

function percentile(values: number[], p: number): number {
  if (values.length === 0) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const idx = Math.ceil((p / 100) * sorted.length) - 1;
  return sorted[Math.max(0, idx)];
}

function mean(values: number[]): number {
  if (!values.length) return 0;
  return values.reduce((a, b) => a + b, 0) / values.length;
}

function spread(range?: { p10: number; p90: number }): number {
  if (!range) return 0;
  return Math.max(0, (range.p90 ?? 0) - (range.p10 ?? 0));
}

function deriveAdaptiveConfig(
  sampleCount: number,
  baselineStability: number,
  baseline: Omit<CalibrationBaseline, 'adaptiveConfig'>,
  eyeOccludedSamples: number,
): AdaptiveCalibrationConfig {
  const sampleConfidence = clamp((sampleCount - 8) / 24, 0, 1);
  const stabilityConfidence = clamp(1 - (baselineStability * 16), 0, 1);
  const calibrationQuality = clamp(0.35 + 0.65 * sampleConfidence * stabilityConfidence, 0.35, 1);

  const sadSpread = mean([
    spread(baseline.frown),
    spread(baseline.browDown),
    spread(baseline.auRanges?.mouthShrugLower),
    spread(baseline.auRanges?.eyeLookDownLeft),
    spread(baseline.auRanges?.eyeLookDownRight),
  ]);
  const angrySpread = mean([
    spread(baseline.browDown),
    spread(baseline.eyeSquint),
    spread(baseline.auRanges?.mouthPressLeft),
    spread(baseline.auRanges?.mouthPressRight),
    spread(baseline.auRanges?.jawForward),
  ]);
  const happySpread = mean([
    spread(baseline.smile),
    spread(baseline.auRanges?.cheekSquintLeft),
    spread(baseline.auRanges?.cheekSquintRight),
  ]);
  const surpriseSpread = mean([
    spread(baseline.eyeWide),
    spread(baseline.browUp),
    spread(baseline.jawOpen),
  ]);

  const sadNorm = clamp(sadSpread / 0.32, 0, 1);
  const angryNorm = clamp(angrySpread / 0.32, 0, 1);
  const happyNorm = clamp(happySpread / 0.32, 0, 1);
  const surpriseNorm = clamp(surpriseSpread / 0.32, 0, 1);

  const adaptScale = 0.10 * calibrationQuality;
  const perEmotionAdaptiveFactors: AdaptiveCalibrationConfig['perEmotionAdaptiveFactors'] = {
    sad: clamp(0.08 + (sadNorm - 0.5) * adaptScale, 0.05, 0.16),
    angry: clamp(0.15 + (angryNorm - 0.5) * adaptScale, 0.10, 0.24),
    happy: clamp(0.15 + (happyNorm - 0.5) * adaptScale, 0.10, 0.24),
    surprise: clamp(0.20 + (surpriseNorm - 0.5) * adaptScale, 0.13, 0.30),
    fear: clamp(0.15 + (surpriseNorm - 0.5) * adaptScale, 0.10, 0.24),
    disgust: clamp(0.15 + (angryNorm - 0.5) * adaptScale, 0.10, 0.24),
  };

  const minScoreDeltas: AdaptiveCalibrationConfig['minScoreDeltas'] = {
    sad: clamp((0.5 - sadNorm) * 0.03 * calibrationQuality, -0.02, 0.03),
    angry: clamp((0.5 - angryNorm) * 0.03 * calibrationQuality, -0.02, 0.03),
    happy: clamp((0.5 - happyNorm) * 0.02 * calibrationQuality, -0.015, 0.02),
    surprise: clamp((0.5 - surpriseNorm) * 0.02 * calibrationQuality, -0.015, 0.02),
  };

  const hasOccludedProfile = eyeOccludedSamples >= 6;
  const occludedCoverage = clamp((eyeOccludedSamples - 6) / 12, 0, 1);
  const persistenceOverrides: AdaptiveCalibrationConfig['persistenceOverrides'] = {
    sadFrames: sadNorm < 0.35 ? 2 : 1,
    angryExtraFrames: hasOccludedProfile ? 0 : (occludedCoverage < 0.35 ? 1 : 0),
    committedHoldFrames: calibrationQuality < 0.55 ? 9 : 8,
  };

  const strongInstantScoreDeltas: AdaptiveCalibrationConfig['strongInstantScoreDeltas'] = {
    sad: clamp((0.5 - sadNorm) * 0.03 * calibrationQuality, -0.015, 0.02),
    angry: clamp((0.5 - angryNorm) * 0.03 * calibrationQuality, -0.015, 0.02),
    happy: clamp((0.5 - happyNorm) * 0.02 * calibrationQuality, -0.01, 0.015),
    surprise: clamp((0.5 - surpriseNorm) * 0.02 * calibrationQuality, -0.01, 0.015),
  };

  return {
    calibrationQuality,
    perEmotionAdaptiveFactors,
    minScoreDeltas,
    persistenceOverrides,
    strongInstantScoreDeltas,
  };
}

// AUs with high natural variance due to head pose and resting muscle tension.
// Using p25 instead of median captures the most relaxed state during calibration,
// making zero-centering more robust across different postures.
const HIGH_VARIANCE_AUS = new Set([
  'mouthShrugLower', 'mouthShrugUpper',
  'eyeLookDownLeft', 'eyeLookDownRight',
  'browOuterUpLeft', 'browOuterUpRight',
  'mouthPressLeft', 'mouthPressRight',
]);

function buildAuProfile(samples: Record<string, number>[]): EyeContextProfile {
  const auMedians: Record<string, number> = {};
  const auRanges: Record<string, { p10: number; p90: number }> = {};
  for (const auKey of CALIBRATION_AUS) {
    const vals = samples.map(sample => sample[auKey] ?? 0).sort((a, b) => a - b);
    if (vals.length > 0) {
      // High-variance AUs use p25 as baseline anchor — more robust than median
      // when the user is not perfectly still during calibration.
      auMedians[auKey] = HIGH_VARIANCE_AUS.has(auKey)
        ? percentile(vals, 25)
        : percentile(vals, 50);
      auRanges[auKey] = { p10: percentile(vals, 10), p90: percentile(vals, 90) };
    }
  }

  return { sampleCount: samples.length, auMedians, auRanges };
}

export function computeStabilityFromSamples(samples: Record<string, number>[], stabilityKeys: string[]): number {
  if (samples.length < 2) return 0.01;
  let totalVariance = 0;
  for (const key of stabilityKeys) {
    const values = samples.map(frame => frame[key] ?? 0);
    const avg = values.reduce((a, b) => a + b, 0) / values.length;
    const variance = values.reduce((a, value) => a + (value - avg) ** 2, 0) / values.length;
    totalVariance += variance;
  }
  return Math.max(totalVariance / stabilityKeys.length, 0.0001);
}

const DRIFT_ALPHA = 0.03;
const DRIFT_MAX_RATIO = 0.30;

export function applyBaselineDrift(
  auMedians: Record<string, number>,
  auRanges: Record<string, { p10: number; p90: number }>,
  originalMedians: Record<string, number>,
  rawBlendshapes: Record<string, number>,
): void {
  for (const key of Object.keys(auMedians)) {
    const raw = rawBlendshapes[key];
    if (raw === undefined) continue;
    const range = auRanges[key];
    const maxDrift = range ? (range.p90 - range.p10) * DRIFT_MAX_RATIO : 0.03;
    const anchor = originalMedians[key] ?? auMedians[key];
    const updated = auMedians[key] * (1 - DRIFT_ALPHA) + raw * DRIFT_ALPHA;
    auMedians[key] = clamp(updated, anchor - maxDrift, anchor + maxDrift);
  }
}

export function finalizeCalibration(
  samples: Record<string, number>[],
  stabilityKeys: string[],
): { baseline: CalibrationBaseline; baselineStability: number } | null {
  if (samples.length === 0) return null;

  const extract = (keys: string[]): number[] =>
    samples.map(sample => avgKeys(sample, keys)).filter(value => value > 0.001);

  const range = (values: number[]) => ({ p10: percentile(values, 10), p90: percentile(values, 90) });

  const globalProfile = buildAuProfile(samples);
  const auMedians = globalProfile.auMedians;
  const auRanges = globalProfile.auRanges;

  const clearSamples = samples.filter((sample: any) => !(sample?.__eyeOccluded));
  const occludedSamples = samples.filter((sample: any) => !!(sample?.__eyeOccluded));
  const clearProfile = clearSamples.length >= 6 ? buildAuProfile(clearSamples) : undefined;
  const occludedProfile = occludedSamples.length >= 6 ? buildAuProfile(occludedSamples) : undefined;

  const computeAuReliability = (profile: EyeContextProfile | undefined, global: EyeContextProfile) => {
    if (!profile) return undefined;
    const reliability: Record<string, number> = {};
    const sampleFactor = Math.max(0.35, Math.min(1, profile.sampleCount / 12));
    for (const auKey of CALIBRATION_AUS) {
      const profileRange = profile.auRanges?.[auKey];
      const globalRange = global.auRanges?.[auKey];
      if (profileRange && globalRange && typeof profileRange.p90 === 'number' && typeof globalRange.p90 === 'number') {
        const profileSpread = Math.max(0, profileRange.p90 - profileRange.p10);
        const globalSpread = Math.max(1e-4, globalRange.p90 - globalRange.p10);
        let value = clamp(profileSpread / globalSpread, 0, 1);
        value = clamp(value * sampleFactor, 0, 1);
        reliability[auKey] = value;
      } else {
        reliability[auKey] = 0;
      }
    }
    return reliability;
  };

  if (clearProfile) clearProfile.auReliability = computeAuReliability(clearProfile, globalProfile);
  if (occludedProfile) occludedProfile.auReliability = computeAuReliability(occludedProfile, globalProfile);

  const baselineCore: Omit<CalibrationBaseline, 'adaptiveConfig'> = {
    smile: range(extract(['mouthSmileLeft', 'mouthSmileRight'])),
    frown: range(extract(['mouthFrownLeft', 'mouthFrownRight'])),
    browUp: range(extract(['browInnerUp', 'browOuterUpLeft', 'browOuterUpRight'])),
    browDown: range(extract(['browDownLeft', 'browDownRight'])),
    eyeWide: range(extract(['eyeWideLeft', 'eyeWideRight'])),
    eyeSquint: range(extract(['eyeSquintLeft', 'eyeSquintRight'])),
    jawOpen: range(extract(['jawOpen'])),
    noseSneer: range(extract(['noseSneerLeft', 'noseSneerRight'])),
    auRanges,
    auMedians,
    eyeContextProfiles: {
      eyeClear: clearProfile,
      eyeOccluded: occludedProfile,
    },
    timestamp: Date.now(),
  };

  const baselineStability = computeStabilityFromSamples(samples, stabilityKeys);
  const adaptiveConfig = deriveAdaptiveConfig(samples.length, baselineStability, baselineCore, occludedSamples.length);
  const baseline: CalibrationBaseline = { ...baselineCore, adaptiveConfig };

  const marVals = extract(['jawOpen']);
  const marBase = range(marVals);
  const logFn = typeof window !== 'undefined' && (window as any)._log ? (window as any)._log : console.log;
  logFn(`[EIE][CALIBRATION] Baseline jawOpen (MAR) p10=${marBase.p10.toFixed(3)} p90=${marBase.p90.toFixed(3)} | All: [${marVals.map(v => v.toFixed(3)).join(', ')}]`);
  logFn('[EIE][CALIBRATION] AU medians:', JSON.stringify(auMedians));
  logFn('[EIE][CALIBRATION][EYE-CONTEXT]', {
    eyeClearSamples: clearSamples.length,
    eyeOccludedSamples: occludedSamples.length,
    eyeClearEnabled: !!clearProfile,
    eyeOccludedEnabled: !!occludedProfile,
    adaptiveQuality: Number(adaptiveConfig.calibrationQuality.toFixed(3)),
  });
  logFn('[EIE][CALIBRATION][ADAPTIVE]', adaptiveConfig);
  logFn('[EIE][CALIBRATION] Baseline:', JSON.stringify(baseline));

  return { baseline, baselineStability };
}
